/**
 * 清新绿意模板 - 列表页
 * 
 * Banner设计：左侧大标题 + 右侧植物插画风格装饰，与深色模板完全不同
 */

import { useEffect, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import {
  getSiteCategories,
  getListings,
  getNavCategories,
  type FrontendCategory,
  type ListingItem,
} from "@/utils/api";
import { useSite } from "@/context/SiteContext";

const SORT_OPTIONS = [
  { key: "latest", label: "最新发布", icon: "ri-time-line" },
  { key: "popular", label: "最受欢迎", icon: "ri-fire-line" },
];

export default function GreenCategoryPage() {
  const { slug } = useParams<{ slug?: string }>();
  const { siteId, loading: siteLoading } = useSite();

  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [navCategories, setNavCategories] = useState<Array<{ id: string; name: string; slug: string; icon: string }>>([]);
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [activeCategoryId, setActiveCategoryId] = useState<string>("");
  const [sortBy, setSortBy] = useState("latest");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 12;

  useEffect(() => {
    if (siteLoading || !siteId) return;
    Promise.all([
      getSiteCategories().then(setCategories).catch(() => {}),
      getNavCategories().then(setNavCategories).catch(() => {}),
    ]);
  }, [siteId, siteLoading]);

  useEffect(() => {
    if (slug && categories.length > 0) {
      const found = categories.find((c) => c.slug === slug || c.id === slug);
      if (found) setActiveCategoryId(found.id);
    }
  }, [slug, categories]);

  useEffect(() => {
    if (siteLoading || !siteId) return;
    setLoading(true);
    const params: Record<string, any> = { page: currentPage, page_size: PAGE_SIZE };
    if (activeCategoryId) params.category_id = activeCategoryId;
    getListings(params)
      .then((data) => {
        setListings(data.list || []);
        setTotal(data.total || 0);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [siteId, siteLoading, activeCategoryId, currentPage]);

  const currentCategoryName = useMemo(() => {
    if (activeCategoryId) {
      const cat = categories.find((c) => c.id === activeCategoryId);
      return cat?.name || "全部分类";
    }
    return "全部分类";
  }, [activeCategoryId, categories]);

  const sortedListings = useMemo(() => {
    const list = [...listings];
    if (sortBy === "popular") list.sort((a, b) => (b.view_count || 0) - (a.view_count || 0));
    return list;
  }, [listings, sortBy]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      {/* ── 页面 Banner：左侧大标题 + 右侧装饰植物插画 ── */}
      <section className="relative bg-gradient-to-r from-emerald-600 via-green-600 to-teal-500 py-12 md:py-16 overflow-hidden">
        {/* 背景装饰：大叶片剪影 */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none select-none">
          <div className="absolute -right-12 -top-20 w-72 h-72 opacity-[0.07]">
            <svg viewBox="0 0 200 200" fill="white" className="w-full h-full">
              <ellipse cx="80" cy="60" rx="50" ry="80" transform="rotate(30 80 60)" />
              <ellipse cx="120" cy="100" rx="45" ry="75" transform="rotate(-20 120 100)" />
              <ellipse cx="150" cy="50" rx="35" ry="70" transform="rotate(45 150 50)" />
            </svg>
          </div>
          <div className="absolute -left-8 bottom-0 w-48 h-48 opacity-[0.05]">
            <svg viewBox="0 0 200 200" fill="white" className="w-full h-full">
              <circle cx="60" cy="80" r="55" />
              <circle cx="140" cy="60" r="40" />
              <circle cx="100" cy="130" r="45" />
            </svg>
          </div>
          {/* 装饰圆点 */}
          <div className="absolute top-12 right-1/4 w-2 h-2 bg-lime-300/40 rounded-full" />
          <div className="absolute top-20 right-[30%] w-1.5 h-1.5 bg-white/30 rounded-full" />
          <div className="absolute bottom-10 right-[45%] w-3 h-3 bg-lime-200/20 rounded-full" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            {/* 左侧：面包屑 + 标题 */}
            <div className="flex-1">
              <nav className="flex items-center gap-1.5 text-sm text-white/60 mb-4">
                <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">
                  <i className="ri-home-4-line" /> 首页
                </Link>
                <i className="ri-arrow-right-s-line text-xs" />
                <span className="text-white/90 font-medium">{currentCategoryName}</span>
              </nav>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white leading-tight tracking-tight">
                {currentCategoryName}
                <span className="block text-lg md:text-xl font-normal text-lime-200/70 mt-2">
                  探索优质服务，发现无限可能
                </span>
              </h1>
            </div>

            {/* 右侧：装饰叶子卡片 */}
            <div className="hidden md:flex items-end gap-3 shrink-0">
              <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center text-3xl">
                🌱
              </div>
              <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center text-2xl">
                🍃
              </div>
              <div className="w-10 h-10 rounded-xl bg-lime-300/20 flex items-center justify-center text-xl">
                🌿
              </div>
            </div>
          </div>
        </div>

        {/* 底部波浪过渡 */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 50" fill="none" className="w-full" preserveAspectRatio="none">
            <path d="M0,25 C240,50 480,0 720,25 C960,50 1200,0 1440,25 L1440,50 L0,50 Z" fill="#faf7f5"/>
          </svg>
        </div>
      </section>

      {/* ── 分类筛选条 ── */}
      <div className="sticky top-[60px] z-20 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => { setActiveCategoryId(""); setCurrentPage(1); }}
              className={`shrink-0 px-4 py-2 rounded-2xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                !activeCategoryId
                  ? "bg-emerald-500 text-white shadow-lg shadow-emerald-200"
                  : "bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-600"
              }`}
            >
              <i className="ri-global-line" />全部
            </button>
            {(navCategories.length > 0 ? navCategories : categories).slice(0, 10).map(cat => (
              <button
                key={cat.id}
                onClick={() => { setActiveCategoryId(cat.id === activeCategoryId ? "" : cat.id); setCurrentPage(1); }}
                className={`shrink-0 px-4 py-2 rounded-2xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
                  activeCategoryId === cat.id
                    ? "bg-emerald-500 text-white shadow-lg shadow-emerald-200"
                    : "bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-600"
                }`}
              >
                <i className={`${cat.icon || 'ri-leaf-line'}`} />{cat.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {/* ── 工具栏 ── */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <i className="ri-filter-2-line text-emerald-400" />
            <span>已为你展示精彩内容</span>
          </div>
          <div className="flex items-center gap-1 bg-white rounded-2xl border border-gray-200 p-1 shadow-sm">
            {SORT_OPTIONS.map(opt => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                  sortBy === opt.key
                    ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-md"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <i className={opt.icon} />{opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Bento Grid 混合尺寸布局 ── */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map(i => (
              <div key={i} className="animate-pulse bg-white rounded-3xl overflow-hidden border border-gray-100">
                <div className="w-full h-44 bg-gray-200" />
                <div className="p-4 space-y-2.5">
                  <div className="w-3/4 h-4 bg-gray-200 rounded-lg" />
                  <div className="w-full h-3 bg-gray-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : sortedListings.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-auto">
            {sortedListings.map((listing) => {

              return (
                <Link
                  key={listing.id}
                  to={`/listing/${listing.id}`}
                  className={`group bg-white rounded-3xl overflow-hidden border border-gray-100 hover:border-emerald-200 hover:shadow-xl transition-all duration-500 hover:-translate-y-1 cursor-pointer`}
                >
                  <div className="relative overflow-hidden bg-gradient-to-br from-emerald-50 to-green-100 aspect-square">
                    {listing.images?.[0] ? (
                      <img src={listing.images[0]} alt={listing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="ri-image-line text-3xl text-emerald-300" />
                      </div>
                    )}
                    {listing.is_pinned && (
                      <span className="absolute top-3 left-3 px-2.5 py-1 bg-emerald-500 text-white text-xs font-bold rounded-xl shadow-sm">
                        <i className="ri-pushpin-line mr-1" />置顶
                      </span>
                    )}
                    <span className="absolute bottom-3 right-3 px-2.5 py-1 bg-black/40 backdrop-blur-sm text-white text-xs rounded-xl">
                      {listing.category?.name}
                    </span>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-gray-800 text-sm line-clamp-2 group-hover:text-emerald-600 transition-colors">
                      {listing.title}
                    </h3>
                    <p className="text-xs text-gray-500 mt-2 line-clamp-2 leading-relaxed">
                      {listing.content}
                    </p>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50 text-xs text-gray-400">
                      <span className="flex items-center gap-1"><i className="ri-user-line" />{listing.contact_name || '匿名'}</span>
                      <div className="flex items-center gap-3">
                        <span>{formatShortDate(listing.created_at)}</span>
                        <span className="flex items-center gap-0.5"><i className="ri-eye-line" />{listing.view_count}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-gray-200">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-3xl bg-emerald-50">
              <i className="ri-search-line text-3xl text-emerald-400" />
            </div>
            <p className="text-gray-500 font-medium text-lg">暂无相关服务</p>
            <p className="text-gray-400 text-sm mt-1">换个分类试试看吧</p>
          </div>
        )}

        {/* ── 分页 ── */}
        {!loading && totalPages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-10 h-10 flex items-center justify-center rounded-2xl bg-white border border-gray-200 text-gray-500 hover:border-emerald-300 hover:text-emerald-600 disabled:opacity-40 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line" />
            </button>

            {generatePagination(currentPage, totalPages).map((p, i) => (
              <button
                key={i}
                onClick={() => typeof p === 'number' ? setCurrentPage(p) : null}
                disabled={typeof p !== 'number'}
                className={`w-10 h-10 flex items-center justify-center rounded-2xl text-sm font-semibold transition-all cursor-pointer ${
                  currentPage === p
                    ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-md"
                    : typeof p === 'number'
                    ? "bg-white border border-gray-200 text-gray-600 hover:border-emerald-300 hover:text-emerald-600"
                    : "text-gray-400"
                }`}
              >
                {typeof p === 'number' ? p : '...'}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-10 h-10 flex items-center justify-center rounded-2xl bg-white border border-gray-200 text-gray-500 hover:border-emerald-300 hover:text-emerald-600 disabled:opacity-40 transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}

function generatePagination(current: number, total: number): (number | string)[] {
  const pages: (number | string)[] = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 3) pages.push('...');
    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
      pages.push(i);
    }
    if (current < total - 2) pages.push('...');
    pages.push(total);
  }
  return pages;
}
